import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { documentsEndpoints } from '@/api/endpoints';
import { callGet, callPut } from '@/api/fetcher';
import { DatasourceDocument } from '@/modules/collections/types';
import { DatasourceType } from '@/shared/types/collections';
import { Pagination } from '@/shared/types/common';

import { onDocumentUpdated } from './useAPIDocuments';
import { queryKeyFactories } from '../../../hooks/utils/queryKeyFactory';

/**
 * Type for the API response that contains draft documents and pagination
 */
interface DraftDocumentsResponse {
  documents: DatasourceDocument[];
  pagination: Pagination;
}

/**
 * Hook parameters interface
 */
interface UseAPIDraftDocumentsParams {
  collection_id?: string;
  datasourceTypes?: DatasourceType[];
  enabled?: boolean;
}

export type CollectionPublish = { document_ids?: string[]; include_all?: boolean };

/**
 * What a publish or draft mutation was asked to change. `datasource_id` is the folder a row menu
 * acted on: those callers know it, and their table does not fetch the draft list the bulk callers
 * derive it from.
 */
type DraftMutationVariables = CollectionPublish & { datasource_id?: string; collectionId?: string };

/** The identity of a document whose caches a publish or draft has to patch. */
type AffectedDocument = Pick<DatasourceDocument, 'collection_id' | 'datasource_id' | 'document_id'>;

/**
 * API methods for the hook
 */
const API = Object.freeze({
  /**
   * Fetch draft documents for a collection
   */
  fetchDraftDocuments: async (collection_id: string): Promise<DraftDocumentsResponse> => {
    return callGet(documentsEndpoints.modalDocuments(collection_id));
  },
  publishDocuments: async (
    collection_id: string,
    options: CollectionPublish
  ): Promise<{ published_count: number }> => {
    return callPut(documentsEndpoints.publishDocuments(collection_id), {
      ...options,
      include_document_ids: options.document_ids,
    });
  },
  draftDocuments: async (
    collection_id: string,
    options: CollectionPublish
  ): Promise<{ drafted_count: number }> => {
    return callPut(documentsEndpoints.draftDocuments(collection_id), {
      ...options,
      include_document_ids: options.document_ids,
    });
  },
});

const onPublishDocumentsUpdated = (queryClient: QueryClient, collection_id: string) => {
  const queryKey = queryKeyFactories.publishDocuments(collection_id);
  queryClient.invalidateQueries({ queryKey });
};

/**
 * The documents a mutation actually changed. A row menu names one document in a folder it knows,
 * so it is taken at its word. Everything else is a bulk action from the publish modal, which has
 * the draft list loaded and can say which of those rows the call covered.
 */
const getAffectedDocuments = (
  draftDocuments: DatasourceDocument[] | undefined,
  variables: DraftMutationVariables,
  collectionId: string
): AffectedDocument[] => {
  if (variables.datasource_id) {
    return (variables.document_ids ?? []).map((document_id) => ({
      collection_id: collectionId,
      datasource_id: variables.datasource_id,
      document_id,
    }));
  }

  if (!draftDocuments) {
    return [];
  }

  return variables.include_all
    ? draftDocuments
    : draftDocuments.filter((doc) => variables.document_ids?.includes(doc.document_id));
};

/**
 * Custom React hook for managing draft documents for collections using React Query
 *
 * This hook provides a unified interface for working with draft documents across
 * different datasource types. It uses React Query for caching and state management.
 *
 * @param collection_id - The collection identifier
 * @param datasourceTypes - Optional array of datasource types to filter by
 * @param enabled - Whether the query should be enabled (defaults to true)
 *
 */
/**
 * Helper function to update individual document query caches
 * This ensures that when documents are published/drafted, their individual
 * queries (used in components like DocumentLine) are updated accordingly
 */
const updateIndividualDocumentCaches = (
  queryClient: QueryClient,
  documents: AffectedDocument[],
  isDraft: boolean
) => {
  documents.forEach((doc) => {
    const documentQueryKey = [
      documentsEndpoints.document(doc.collection_id, doc.datasource_id, doc.document_id),
    ];

    // Update the individual document cache if it exists
    queryClient.setQueryData<DatasourceDocument>(documentQueryKey, (oldData) => {
      if (!oldData) {
        return oldData;
      }
      return { ...oldData, is_draft: isDraft };
    });
  });
};

export const useAPIDraftDocuments = ({
  collection_id,
  enabled = true,
}: UseAPIDraftDocumentsParams) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // Generate query key
  const queryKey = queryKeyFactories.publishDocuments(collection_id);

  // Fetch data
  const { data: response, isLoading } = useQuery<DraftDocumentsResponse, Error>({
    queryKey,
    queryFn: () => API.fetchDraftDocuments(collection_id),
    enabled: !!collection_id && enabled,
    staleTime: 0, // Always fetch fresh data
    refetchOnWindowFocus: false,
    refetchOnMount: true, // Refetch when modal opens
  });

  const { mutateAsync: publishDocuments, isPending: isPublishing } = useMutation({
    mutationFn: async ({ document_ids, include_all, collectionId }: DraftMutationVariables) => {
      return API.publishDocuments(collectionId ?? collection_id, { document_ids, include_all });
    },
    onSuccess: async (resp, variables) => {
      const published_count = resp.published_count;
      onPublishDocumentsUpdated(queryClient, collection_id);

      // Bulk publish can affect rows across tabs and folders, so refetch the lists
      const publishedCollectionId = variables.collectionId ?? collection_id;
      queryClient.invalidateQueries({
        queryKey: queryKeyFactories.collectionDatasourcesDocuments(publishedCollectionId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeyFactories.collectionFolderDocuments(publishedCollectionId),
      });

      // Patch the rows that are already on screen so the badge flips without waiting for a refetch
      const affectedDocuments = getAffectedDocuments(
        response?.documents,
        variables,
        publishedCollectionId
      );
      affectedDocuments.forEach((doc) => {
        onDocumentUpdated(queryClient, doc.collection_id, doc.datasource_id, doc.document_id, {
          is_draft: false,
        });
      });
      updateIndividualDocumentCaches(queryClient, affectedDocuments, false);

      toast.success(t('collections.publish_success', { count: published_count }));
    },
    meta: { errorToast: true },
  });

  const { mutateAsync: draftDocuments } = useMutation({
    mutationFn: async ({ document_ids, include_all }: DraftMutationVariables) => {
      return API.draftDocuments(collection_id, { document_ids, include_all });
    },
    onSuccess: async (resp, variables) => {
      const drafted_count = resp.drafted_count;
      onPublishDocumentsUpdated(queryClient, collection_id);

      // Bulk draft can affect rows across tabs and folders, so refetch the lists
      queryClient.invalidateQueries({
        queryKey: queryKeyFactories.collectionDatasourcesDocuments(collection_id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeyFactories.collectionFolderDocuments(collection_id),
      });

      // Patch the rows that are already on screen so the badge flips without waiting for a refetch
      const affectedDocuments = getAffectedDocuments(response?.documents, variables, collection_id);
      affectedDocuments.forEach((doc) => {
        onDocumentUpdated(queryClient, doc.collection_id, doc.datasource_id, doc.document_id, {
          is_draft: true,
        });
      });
      updateIndividualDocumentCaches(queryClient, affectedDocuments, true);

      toast.success(t('collections.draft_success', { count: drafted_count }));
    },
    meta: { errorToast: true },
  });

  return {
    data: response?.documents || [],
    isLoading,
    publishDocuments,
    isPublishing,
    draftDocuments,
  };
};
